import { RichTextEditor as MantineRte } from '@mantine/tiptap'
import Link from '@tiptap/extension-link'
import Underline from '@tiptap/extension-underline'
import { useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { useEffect } from 'react'

interface Props {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  minHeight?: number
}

/**
 * Mantine Tiptap-based rich text editor. Emits HTML string.
 * Toolbar: B, I, S, link, heading, quote, lists, undo/redo.
 */
export function RichTextEditor({ value, onChange, minHeight = 140 }: Props) {
  const editor = useEditor({
    extensions: [StarterKit, Underline, Link.configure({ openOnClick: false })],
    content: value,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  })

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false })
    }
  }, [editor, value])

  return (
    <MantineRte editor={editor}>
      <MantineRte.Toolbar sticky stickyOffset={0}>
        <MantineRte.ControlsGroup>
          <MantineRte.Bold />
          <MantineRte.Italic />
          <MantineRte.Underline />
          <MantineRte.Strikethrough />
          <MantineRte.Code />
        </MantineRte.ControlsGroup>
        <MantineRte.ControlsGroup>
          <MantineRte.H1 />
          <MantineRte.H2 />
          <MantineRte.H3 />
        </MantineRte.ControlsGroup>
        <MantineRte.ControlsGroup>
          <MantineRte.Blockquote />
          <MantineRte.BulletList />
          <MantineRte.OrderedList />
        </MantineRte.ControlsGroup>
        <MantineRte.ControlsGroup>
          <MantineRte.Link />
          <MantineRte.Unlink />
        </MantineRte.ControlsGroup>
        <MantineRte.ControlsGroup>
          <MantineRte.Undo />
          <MantineRte.Redo />
        </MantineRte.ControlsGroup>
      </MantineRte.Toolbar>
      <MantineRte.Content style={{ minHeight }} />
    </MantineRte>
  )
}
